import React, { useEffect, useRef, useState } from "react";

// AnimatedCounter, counts up from `from` to `to` once it scrolls into view.
// Supports a numeric prefix/suffix (e.g. "₹", "M+", "%").
// Pass the original display string in `display` (e.g. "10M+", "4.9★", "500+")
// and the numeric target in `to`. Component will animate then render full string.
// `from` defaults to 0 but should be set to a sensible floor for ratings (e.g.
// from={4} for a 4.9★ counter) so the visible animation never passes through
// implausible mid-values like 2.1★.
export default function AnimatedCounter({ to, from = 0, suffix = "", prefix = "", duration = 1400, decimals = 0, className = "", testId }) {
    const ref = useRef(null);
    const [count, setCount] = useState(from);
    const [done, setDone] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !done) {
                        const start = performance.now();
                        const tick = (now) => {
                            const t = Math.min(1, (now - start) / duration);
                            const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
                            setCount(from + eased * (to - from));
                            if (t < 1) requestAnimationFrame(tick);
                            else setDone(true);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.4 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [to, from, duration, done]);

    const display = decimals ? count.toFixed(decimals) : Math.floor(count).toLocaleString("en-IN");

    return (
        <span ref={ref} className={className} data-testid={testId}>
            {prefix}
            {display}
            {suffix}
        </span>
    );
}
